import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HiLink, HiBadgeCheck } from "react-icons/hi";
import { formatExperienceFromDays } from '../../../utils/skillDataTransformer';
import Tooltip from '../../shared/Tooltip';
import { RichTextContent } from '../../../utils/richTextProcessor';
import { sortByEndDateDesc } from '../../../utils/sortByDate';
import { createProfileViewRequestAction } from '../../../redux/actions/userActions';
import { getSentProfileViewRequestsAction } from '../../../redux/actions/profileViewRequestActions';
import { useToast } from '../../../contexts/ToastContext';

const SkillsTab = ({ 
  selectedSkill, 
  setSelectedSkill, 
  isTopSkillsExpanded, 
  setIsTopSkillsExpanded,
  isSkillsExpanded, 
  setIsSkillsExpanded,
  expandedProjects,
  setExpandedProjects,
  isMainProjectExpanded,
  setIsMainProjectExpanded,
  setVerificationOverlay,
  skillData, // New prop for transformed data
  isPrivateProfile = false,
  profileUserId = null,
  isAuthenticated = true,
  onRequireAuth = null,
  isViewingOwnProfile = false,
}) => {
  const dispatch = useDispatch();
  const { success: showSuccess, error: showError } = useToast();
  const [requestLoading, setRequestLoading] = useState(false);
  
  // Get sent profile view requests from Redux
  const sentProfileViewRequests = useSelector((state) => state.user?.sentProfileViewRequests || []);

  // Fetch sent requests on mount if authenticated
  useEffect(() => {
    if (isAuthenticated && profileUserId && !isViewingOwnProfile) {
      dispatch(getSentProfileViewRequestsAction(null, 25, 0));
    }
  }, [dispatch, isAuthenticated, profileUserId, isViewingOwnProfile]);

  // Find existing profile view request for this profile owner
  const existingProfileViewRequest = useMemo(() => {
    if (!profileUserId || !sentProfileViewRequests || sentProfileViewRequests.length === 0 || isViewingOwnProfile) {
      return null;
    }
    
    const found = sentProfileViewRequests.find((request) => {
      const profileOwnerId = request.profileOwner?._id || request.profileOwner;
      return profileOwnerId && String(profileOwnerId) === String(profileUserId) && request.status === "pending";
    });
    
    return found;
  }, [sentProfileViewRequests, profileUserId, isViewingOwnProfile]);

  const handleRequestAccess = async () => {
    if (!isAuthenticated) {
      if (onRequireAuth) {
        onRequireAuth("auth_required");
      }
      return;
    }

    if (!profileUserId || isViewingOwnProfile) return;

    setRequestLoading(true);
    try {
      const result = await dispatch(createProfileViewRequestAction(profileUserId));
      if (result?.success) {
        showSuccess("Profile view request sent successfully");
        // Refresh sent requests to update button state
        await dispatch(getSentProfileViewRequestsAction(null, 25, 0));
      } else {
        showError(result?.error || "Failed to send request");
      }
    } catch (error) {
      showError("Error sending request");
    } finally {
      setRequestLoading(false);
    }
  };

  const [expandedCardIndex, setExpandedCardIndex] = useState(null);
  const [isApplicationExpanded, setIsApplicationExpanded] = useState(false);
  const [isAcquisitionExpanded, setIsAcquisitionExpanded] = useState(false);
  // Use skillData from API
  const top5Skills = skillData?.top5Skills || [];
  const otherSkills = skillData?.otherSkills || [];

  // Get selected skill details
  const selectedSkillDetails = skillData?.skillDetails?.find(s => s.name === selectedSkill);
  const selectedAppliedApplications = skillData?.appliedApplications?.find(a => a.skillName === selectedSkill);
  const selectedAcquiredApplications = skillData?.acquiredApplications?.find(a => a.skillName === selectedSkill);

  // When profile is private, hide detailed content and show lock message
  if (isPrivateProfile) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
        {/* Left - Skills list area replaced with lock */}
        <div className="col-span-1 bg-white rounded-lg border border-gray-200 shadow-sm p-6 flex flex-col items-center justify-center text-center">
          <svg
            className="w-8 h-8 text-amber-500 mb-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 11c-1.105 0-2 .895-2 2v3a2 2 0 104 0v-3a2 2 0 00-2-2z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 11V9a5 5 0 10-10 0v2M7 11h10a2 2 0 012 2v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4a2 2 0 012-2z"
            />
          </svg>
          <h3 className="text-base md:text-lg font-bold text-gray-900 font-heading mb-1">
            Private account
          </h3>
          <p className="text-xs md:text-sm text-gray-600 font-roboto-flex mb-3">
            This is a private account.
          </p>
          {!isViewingOwnProfile && (
            <button
              onClick={handleRequestAccess}
              disabled={requestLoading || existingProfileViewRequest}
              className={`w-full px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                existingProfileViewRequest
                  ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                  : "bg-primary-700 text-white hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed"
              }`}
            >
              {requestLoading ? (
                "Sending..."
              ) : existingProfileViewRequest ? (
                "Request Pending"
              ) : (
                "Request MYST Access"
              )}
            </button>
          )}
        </div>

        {/* Right - Skill details & experience area replaced with lock */}
        <div className="col-span-1 md:col-span-3 bg-white rounded-lg border border-gray-200 shadow-sm p-8 flex flex-col items-center justify-center text-center">
          <svg
            className="w-10 h-10 text-amber-500 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 11c-1.105 0-2 .895-2 2v3a2 2 0 104 0v-3a2 2 0 00-2-2z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 11V9a5 5 0 10-10 0v2M7 11h10a2 2 0 012 2v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4a2 2 0 012-2z"
            />
          </svg>
          <h2 className="text-lg md:text-xl font-bold text-gray-900 font-heading mb-2">
            Private account
          </h2>
          <p className="text-sm text-gray-600 font-roboto-flex max-w-md">
            This is a private account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
      {/* Left Card - Skills List */}
      <div className="col-span-1 bg-white rounded-lg border border-gray-200 shadow-sm p-3 md:p-3 accordion-section">
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-base md:text-lg font-bold text-gray-900 font-heading">
            Skills
          </h3>
          {isPrivateProfile && (
            <svg
              className="w-4 h-4 text-amber-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 11c-1.105 0-2 .895-2 2v3a2 2 0 104 0v-3a2 2 0 00-2-2z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 11V9a5 5 0 10-10 0v2M7 11h10a2 2 0 012 2v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4a2 2 0 012-2z"
              />
            </svg>
          )}
        </div>
        
        {/* Top 5 Skills */}
        <div className="mb-2 accordion-list">
          <button
            onClick={() => setIsTopSkillsExpanded(!isTopSkillsExpanded)}
            className={`accordion-button w-full rounded-md text-xs md:text-sm font-bold text-gray-800 font-heading hover:bg-gray-50 transition-colors border-b border-gray-200 pb-2 ${
              isTopSkillsExpanded ? 'expanded' : ''
            }`}
            aria-expanded={isTopSkillsExpanded}
          >
            <span className="accordion-header">Top 5 Skills</span>
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          {isTopSkillsExpanded && (
            <div className="space-y-2 mt-2 font-flex">
              {top5Skills.map((skill, index) => (
                <button
                  key={skill}
                  onClick={() => setSelectedSkill(skill)}
                  className={`w-full text-left py-1.5 px-2 rounded-md text-sm transition-all duration-200 tooltip-item font-roboto-flex ${
                    selectedSkill === skill
                      ? "text-primary font-bold"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                  data-tooltip={skill}
                  title={skill}
                >
                  <div className="flex items-center gap-2">
                    <div className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 border border-primary-200 shadow-sm rounded-full flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="truncate">{skill}</div>
                  </div>
                  
                </button>
              ))}
            </div>
          )}
        </div>
        
        {/* Other Skills */}
        <div className="mt-2 accordion-list">
          <button
            onClick={() => setIsSkillsExpanded(!isSkillsExpanded)}
            className={`accordion-button w-full rounded-md text-xs md:text-sm font-semibold text-gray-800 font-heading hover:bg-gray-50 transition-colors border-b border-gray-200 pb-2 ${
              isSkillsExpanded ? 'expanded' : ''
            }`}
            aria-expanded={isSkillsExpanded}
          >
            <span className="accordion-header">Other Skills</span>
            <svg
              className="w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          {isSkillsExpanded && (
            <div className="space-y-2 mt-2">
              {otherSkills.map((skill, index) => (
                <button
                  key={skill}
                  onClick={() => setSelectedSkill(skill)}
                  className={`w-full text-left py-1.5 px-2 rounded-md text-sm transition-all duration-200 font-body tooltip-item ${
                    selectedSkill === skill
                      ? "text-primary font-bold"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                  data-tooltip={skill}
                  title={skill}
                >
                  {skill}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* Right Card - Skill Details & Application */}
      <div className="col-span-1 md:col-span-3 space-y-3">
        {/* Skill Header Card */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
          {selectedSkill ? (
            <div>
              {/* Skill Header - no verification or PDF link */}
              <div className="mb-0">
                <div className="flex items-center justify-between">
                  {(() => {
                    const top5Index = top5Skills.findIndex(skill => skill === selectedSkill);
                    const otherIndex = otherSkills.findIndex(skill => skill === selectedSkill);
                    
                    if (top5Index !== -1) {
                      const rankText = top5Index === 0 ? "1st" : 
                                     top5Index === 1 ? "2nd" : 
                                     top5Index === 2 ? "3rd" : 
                                     `${top5Index + 1}th`;
                      return (
                        <>
                          <h3 className="text-lg font-bold text-gray-900 font-heading">
                            {selectedSkill}
                          </h3>
                          <div className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-700 text-sm font-semibold rounded-md border border-primary-200">
                            #{rankText} Top Skill
                          </div>
                        </>
                      );
                    } else if (otherIndex !== -1) {
                      return (
                        <h3 className="text-lg font-bold text-gray-900 font-heading">
                          {selectedSkill}
                        </h3>
                      );
                    }
                    return (
                      <h3 className="text-lg font-bold text-gray-900 font-heading">
                        {selectedSkill}
                      </h3>
                    );
                  })()}
                </div>
              </div>
              
              
            </div>
          ) : (
            <div className="text-center text-gray-500 py-4">
              <p className="text-sm font-medium font-flex-regular">Select a skill from the left to view details</p>
            </div>
          )}
        </div>
        
        {/* Application of Skills Card */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
          <div 
            className="flex items-center justify-between -mb-1 cursor-pointer hover:bg-gray-50 rounded-md p-2 -m-2 transition-colors"
            onClick={() => setIsApplicationExpanded(!isApplicationExpanded)}
          >
            <div className="flex items-center gap-3">
              <h4 className="font-semibold text-gray-800 font-heading text-base flex items-center gap-1">
                {isPrivateProfile && (
                  <svg
                    className="w-3.5 h-3.5 text-amber-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 11c-1.105 0-2 .895-2 2v3a2 2 0 104 0v-3a2 2 0 00-2-2z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 11V9a5 5 0 10-10 0v2M7 11h10a2 2 0 012 2v4a2 2 0 01-2 2H7a2 2 0 01-2-2z"
                    />
                  </svg>
                )}
                Application of the Skill
              </h4>
              <span className="text-sm text-gray-600 font-medium font-flex-regular">
                Total Applied Experience:{" "}
                {selectedSkillDetails
                  ? formatExperienceFromDays(
                      selectedSkillDetails.experience.totalDays
                    )
                  : "No experience data available"}
              </span>
            </div>
            <div className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors">
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${isApplicationExpanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
          
          {/* Dynamic Project Cards */}
          {isApplicationExpanded && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
            {selectedAppliedApplications?.applications && selectedAppliedApplications.applications.length > 0 ? [...sortByEndDateDesc(selectedAppliedApplications.applications)].map((application, index) => {
              const isCardExpanded = expandedCardIndex === `applied-${index}`;
              const hasAnyExpanded = expandedCardIndex && expandedCardIndex.startsWith('applied-');
              const shouldShow = !hasAnyExpanded || isCardExpanded;
              
              if (!shouldShow) return null;
              
              return (
                <div 
                  key={`applied-${index}`}
                  className={`bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-all duration-300 ${
                    isCardExpanded ? 'col-span-2 p-4' : 'p-2'
                  }`}
                  onClick={() => {
                    if (isCardExpanded) {
                      setExpandedCardIndex(null);
                      setVerificationOverlay({ show: false });
                      return;
                    }
                    setVerificationOverlay({ show: false });
                    setExpandedCardIndex(`applied-${index}`);
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-semibold text-gray-900 font-heading text-sm leading-tight">
                      {application.title}
                    </h5>
                    <div className="flex items-center gap-2">
                      {isCardExpanded && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedCardIndex(null);
                            setVerificationOverlay({ show: false });
                          }}
                          className="cursor-pointer hover:scale-110 transition-transform duration-200"
                        >
                          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                      {isCardExpanded && (application.validationData && application.validationData.length > 0) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setVerificationOverlay({
                              show: true,
                              title: application.title,
                              validationData: application.validationData || []
                            });
                          }}
                          className="cursor-pointer hover:scale-110 transition-transform duration-200"
                        >
                          <HiBadgeCheck className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        </button>
                      )}
                      {isCardExpanded && application.attachmentExists && (
                        <Tooltip text="Attachment available on MYST">
                          <HiLink className="w-4 h-4 text-gray-600" />
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  <p className="font-flex-regular text-gray-500 text-xs">{application.organization}</p>
                  <p className="font-flex-regular text-gray-500 text-xs">{application.location} {application.duration}</p>
                  
                  {isCardExpanded && (
                    <div className="mt-2 space-y-2">
                      {application.description && (
                        <RichTextContent 
                          content={application.description}
                          className="font-flex-regular text-sm text-gray-700 leading-relaxed"
                        />
                      )}
                      {application.impact && (
                        <RichTextContent 
                          content={`Impact: ${application.impact}`}
                          className="font-flex-regular text-sm text-gray-700 font-medium"
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            }) : (
              <div className="col-span-2 text-center text-gray-500 py-4">
                <p className="text-sm font-medium font-flex-regular">No Skills Applications found</p>
              </div>
            )}
            </div>
          )}
        </div>

        {/* Acquisition of Skills Card */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3">
          <div 
            className="flex items-center justify-between -mb-1 cursor-pointer hover:bg-gray-50 rounded-md p-2 -m-2 transition-colors"
            onClick={() => setIsAcquisitionExpanded(!isAcquisitionExpanded)}
          >
            <div className="flex items-center gap-3">
              <h4 className="font-semibold text-gray-800 font-heading text-base flex items-center gap-1">
                {isPrivateProfile && (
                  <svg
                    className="w-3.5 h-3.5 text-amber-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 11c-1.105 0-2 .895-2 2v3a2 2 0 104 0v-3a2 2 0 00-2-2z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 11V9a5 5 0 10-10 0v2M7 11h10a2 2 0 012 2v4a2 2 0 01-2 2H7a2 2 0 01-2-2z"
                    />
                  </svg>
                )}
                Acquisition of the Skill
              </h4>
              <span className="text-sm text-gray-600 font-medium font-flex-regular">
                Total Acquired Experience: {selectedSkillDetails && selectedSkillDetails.acquiredExperience ? 
                  formatExperienceFromDays(selectedSkillDetails.acquiredExperience.totalDays) : 
                  (selectedSkillDetails ? formatExperienceFromDays(0) : "No experience data available")
                }
              </span>
            </div>
            <div className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors">
              <svg
                className={`w-4 h-4 transition-transform duration-200 ${isAcquisitionExpanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
          
          {/* Dynamic Learning Cards */}
          {isAcquisitionExpanded && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
            {selectedAcquiredApplications?.applications && selectedAcquiredApplications.applications.length > 0 ? [...sortByEndDateDesc(selectedAcquiredApplications.applications)].map((application, index) => {
              const isCardExpanded = expandedCardIndex === `acquired-${index}`;
              const hasAnyExpanded = expandedCardIndex && expandedCardIndex.startsWith('acquired-');
              const shouldShow = !hasAnyExpanded || isCardExpanded;
              
              if (!shouldShow) return null;
              
              return (
                <div 
                  key={`acquired-${index}`}
                  className={`bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-all duration-300 ${
                    isCardExpanded ? 'col-span-2 p-4' : 'p-2'
                  }`}
                  onClick={() => {
                    if (isCardExpanded) {
                      setExpandedCardIndex(null);
                      setVerificationOverlay({ show: false });
                      return;
                    }
                    setVerificationOverlay({ show: false });
                    setExpandedCardIndex(`acquired-${index}`);
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h5 className="font-semibold text-gray-900 font-heading text-sm leading-tight">
                      {application.title}
                    </h5>
                    <div className="flex items-center gap-2">
                      {isCardExpanded && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedCardIndex(null);
                            setVerificationOverlay({ show: false });
                          }}
                          className="cursor-pointer hover:scale-110 transition-transform duration-200"
                        >
                          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                      {isCardExpanded && (application.validationData && application.validationData.length > 0) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setVerificationOverlay({
                              show: true,
                              title: application.title,
                              validationData: application.validationData || []
                            });
                          }}
                          className="cursor-pointer hover:scale-110 transition-transform duration-200"
                        >
                          <HiBadgeCheck className="w-4 h-4 text-blue-600 flex-shrink-0" />
                        </button>
                      )}
                      {isCardExpanded && application.attachmentExists && (
                        <Tooltip text="Attachment available on MYST">
                          <HiLink className="w-4 h-4 text-gray-600" />
                        </Tooltip>
                      )}
                    </div>
                  </div>
                  <p className="font-flex-regular text-gray-500 text-xs">{application.organization}</p>
                  <p className="font-flex-regular text-gray-500 text-xs">{application.location} {application.duration}</p>
                  
                  {isCardExpanded && (
                    <div className="mt-2 space-y-2">
                      {application.description && (
                        <RichTextContent 
                          content={application.description}
                          className="font-flex-regular text-sm text-gray-700 leading-relaxed"
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            }) : (
              <div className="col-span-2 text-center text-gray-500 py-4">
                <p className="text-sm font-medium font-flex-regular">No Skills Acquisitions found</p>
              </div>
            )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SkillsTab;
